export interface TooltipGeometry {
    tooltipWidth: number;
    tooltipHeight: number;
    arrowHalfWidth: number;
}

export interface TooltipDOMRefs {
    tooltip: HTMLDivElement | null;
    arrow: HTMLSpanElement | null;
}

export const DEFAULT_TOOLTIP_WIDTH = 350;
export const DEFAULT_TOOLTIP_MARGIN = 15;

export const measureTooltipGeometry = (
    refs: TooltipDOMRefs,
    prev: TooltipGeometry | null = null,
): TooltipGeometry => {
    const { tooltip, arrow } = refs;
    const width = tooltip?.clientWidth || prev?.tooltipWidth || DEFAULT_TOOLTIP_WIDTH;
    const height = tooltip?.clientHeight || prev?.tooltipHeight || 0;

    let arrowHalfWidth = prev?.arrowHalfWidth ?? 6;
    if (arrow) {
        const cs = getComputedStyle(arrow);
        const bw = parseFloat(cs.borderLeftWidth || '6');
        if (!Number.isNaN(bw)) arrowHalfWidth = bw;
    }

    return { tooltipWidth: width, tooltipHeight: height, arrowHalfWidth };
};

export interface TooltipPositionParams {
    pageX: number;
    hostRect: DOMRect;
    geometry: TooltipGeometry;
    isTouchDevice: boolean;
    minLeft?: number;
    margin?: number;
}

export interface TooltipComputedPosition {
    left: number;
    top: number;
    arrowLeft: number | null;
}

export const computeTooltipPosition = ({
    pageX,
    hostRect,
    geometry,
    isTouchDevice,
    minLeft = 10,
    margin = DEFAULT_TOOLTIP_MARGIN,
}: TooltipPositionParams): TooltipComputedPosition => {
    const isTinyViewport = window.innerWidth < 400;
    const { tooltipWidth, tooltipHeight } = geometry;

    let desiredLeft = pageX - window.pageXOffset - tooltipWidth / 2;
    if (isTinyViewport && desiredLeft >= window.innerWidth - tooltipWidth) {
        desiredLeft = 0;
    }
    const clampedLeft = Math.max(desiredLeft, minLeft);

    const top = hostRect.top + window.scrollY - (tooltipHeight + (isTouchDevice ? 5 : margin));

    const arrowHalf = geometry.arrowHalfWidth;
    const tipX = pageX - window.pageXOffset - clampedLeft;
    const maxLeft = tooltipWidth - 2 * arrowHalf;
    const arrowLeft = Math.max(Math.min(tipX - arrowHalf, maxLeft), 0);

    return {
        left: Math.round(clampedLeft),
        top: Math.round(top),
        arrowLeft: Math.round(arrowLeft),
    };
};
